import React from 'react'
import { clsx } from 'clsx'

/**
 * Button 组件的 props 接口
 */
interface ButtonProps {
  /** 按钮文本或子元素 */
  children: React.ReactNode
  /** 按钮类型 */
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger'
  /** 按钮大小 */
  size?: 'sm' | 'md' | 'lg'
  /** 是否禁用 */
  disabled?: boolean
  /** 是否为加载状态 */
  loading?: boolean
  /** 按钮类型 */
  type?: 'button' | 'submit' | 'reset'
  /** 点击事件处理器 */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** 自定义类名 */
  className?: string
  /** 其他 HTML button 属性 */
  [key: string]: any
}

/**
 * 基础按钮组件
 * 支持多种变体、大小和状态
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  className,
  ...props
}, ref) => {
  /**
   * 获取按钮样式（使用CSS变量）
   */
  const getButtonStyles = () => {
    const baseStyles = clsx(
      // 基础样式
      'inline-flex items-center justify-center font-medium transition-all duration-200',
      'focus:outline-none',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'relative overflow-hidden',
      
      // 大小样式
      {
        'px-3 py-1.5 text-sm rounded-md': size === 'sm',
        'px-4 py-2 text-sm rounded-lg': size === 'md',
        'px-6 py-3 text-base rounded-xl': size === 'lg',
      },
      
      // 加载状态
      {
        'cursor-wait': loading,
      },
      
      className
    )

    // 根据变体设置样式
    let variantStyles = {}
    switch (variant) {
      case 'primary':
        variantStyles = {
          background: 'var(--accent-blue)',
          color: 'white',
          border: 'none'
        }
        break
      case 'secondary':
        variantStyles = {
          background: 'var(--bg-hover)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-light)'
        }
        break
      case 'ghost':
        variantStyles = {
          background: 'transparent',
          color: 'var(--text-primary)',
          border: 'none'
        }
        break
      case 'outline':
        variantStyles = {
          background: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-medium)'
        }
        break
      case 'danger':
        variantStyles = {
          background: '#dc3545',
          color: 'white',
          border: 'none'
        }
        break
      default:
        variantStyles = {}
    }

    return { className: baseStyles, style: variantStyles }
  }

  /**
   * 获取hover样式
   */
  const getHoverStyles = () => {
    const hoverStyles: Record<string, any> = {
      primary: { background: '#1a6dc7' },
      secondary: { background: 'var(--bg-active)' },
      ghost: { background: 'var(--bg-hover)' },
      outline: { background: 'var(--bg-hover)' },
      danger: { background: '#c82333' }
    }
    return hoverStyles[variant] || {}
  }

  /**
   * 渲染加载指示器
   */
  const renderLoader = () => {
    if (!loading) return null
    
    return (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )
  }

  /**
   * 处理点击事件
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      event.preventDefault()
      return
    }
    
    onClick?.(event)
  }

  const buttonStyles = getButtonStyles()

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      onClick={handleClick}
      className={buttonStyles.className}
      style={buttonStyles.style}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.currentTarget.style, getHoverStyles())
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.currentTarget.style, buttonStyles.style)
        }
      }}
      {...props}
    >
      {renderLoader()}
      {children}
    </button>
  )
})

/**
 * Button 组件的显示名称
 */
Button.displayName = 'Button'

/**
 * 导出 ButtonProps 类型
 */
export type { ButtonProps }